import { ExternalLink, EyeOff, X } from 'react-feather';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { EventHeader as EventHeaderImage, EventInfo } from '../../common/Common/EventHeader';
import { SportType } from '../../__generated__/graphql';
import { assetUrl, resizedImageUrl } from '../../common/helpers';
import CookieConsentBar from '../../common/Common/CookieConsentBar';
import UI from '../../common/UI';
import config from '../config';
import useLocale from '../../common/useLocale';

interface Event {
  id: string;
  title: string;
  logo?: {
    id: string;
    url: string;
  } | null;
  header?: {
    id: string;
    url: string;
  } | null;
  checkout_url: string;
  start_date: string;
  end_date: string;
  location?: {
    id?: string | null;
    description?: string | null;
  } | null;
  website_url?: string | null;
  email?: string | null;
  brand_color?: string | null;
  active: boolean;
  ticket_categories: {
    sport_type?: SportType | null;
  }[];
}

interface Project {
  id: string;
  google_analytics?: string | null;
  facebook_pixel?: string | null;
  organisation_name: string;
  organisation_address?: string | null;
  organisation_zip_code?: string | null;
  organisation_city?: string | null;
  organisation_country?: string | null;
  organisation_email?: string | null;
  privacy_statement?: string | null;
}

interface LayoutProps {
  event: Event & {
    project: Project;
  };
  embedded: boolean;
  children?: ReactNode;
}

const Layout = ({
  event,
  embedded,
  children,
}: LayoutProps) => {
  const { t } = useTranslation();

  return (
    <Container>
      {!embedded && (
        <CookieConsentBar />
      )}

      {!event.active && (
        <UI.Card
          sc={{
            background: 'tertiary',
            color: 'white',
            mt: [-3, embedded ? -5 : 0],
            mx: [-3, embedded ? -5 : 0],
            borderRadius: ['none', embedded ? 'none' : 'xl'],
          }}
        >
          <UI.H4 sc={{ color: 'white' }}>
            <UI.Icon>
              <EyeOff />
            </UI.Icon>
            {' '}
            {t('layout.event_inactive')}
          </UI.H4>
          {t('layout.event_inactive_description')}
        </UI.Card>
      )}

      <EventHeader event={event} embedded={embedded} />

      {children}

      <UI.HR />

      <EventFooter event={event} />

      <UI.HR />

      <AtletaFooter />
    </Container>
  );
};

export const SectionTitle = styled(UI.H3)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 400;
  `}
`;

export const Container = styled(UI.GridContainer)`
  ${({ theme }) => css`
    min-width: 320px;
    max-width: ${theme.breakpoints.md}px;
    margin: 0 auto;
    padding: ${theme.gutter * 0.75}px ${theme.gutter * 0.5}px;

    @media ${theme.devices.md} {
      padding: ${theme.gutter * 1.25}px;
    }
  `}
`;

export const EventHeader = ({
  event,
  embedded,
}: {
  event: Event;
  embedded?: boolean;
}) => {
  const sportType = event.ticket_categories.find((ticketCategory) => !!ticketCategory.sport_type)?.sport_type;

  return (
    <>
      {event.header && (
        <EventHeaderImage
          headerUrl={event.header.url}
          sportType={sportType}
          alt={event.title}
        />
      )}
      <UI.GridContainer
        sc={{
          columns: event.logo ? ['48px 1fr', '60px 1fr'] : '1fr',
          gutter: 0.5,
          alignVertical: 'center',
        }}
      >
        {event.logo && (
          <UI.Div>
            <UI.A href={!embedded ? `${event.checkout_url}` : undefined}>
              <UI.ProfilePic
                sc={{ background: 'white', size: [48, 60] }}
              >
                <img src={resizedImageUrl(event.logo.url, 300)} alt={event.title} />
              </UI.ProfilePic>
            </UI.A>
          </UI.Div>
        )}
        <UI.Div sc={{ small: [true, false] }}>
          <UI.H1 sc={{ fontSize: [2, 4], mb: 0.5 }} style={{ fontWeight: 700 }}>
            <UI.A href={!embedded ? `${event.checkout_url}` : undefined} sc={{ color: 'gray.800' }}>
              {event.title}
            </UI.A>
          </UI.H1>
          <EventInfo event={event} />
        </UI.Div>
      </UI.GridContainer>
    </>
  );
};

export interface OrganizerInfoProps {
  event: Event & {
    project: Project;
  };
}

export const OrganizerInfo = ({ event }: OrganizerInfoProps) => {
  const { project } = event;

  return (
    <>
      <strong>
        {project.organisation_name}
      </strong>
      {!event.email && project.organisation_email && (
        <UI.Div sc={{ mt: 2 }}>
          <UI.A href={`mailto:${project.organisation_email}`}>
            {project.organisation_email}
          </UI.A>
        </UI.Div>
      )}
      {event.email && (
        <UI.Div sc={{ mt: 2 }}>
          <a href={`mailto:${event.email}`}>
            {event.email}
          </a>
        </UI.Div>
      )}
    </>
  );
};

interface EventFooterProps {
  event: Event & {
    project: Project;
  };
}

export const EventFooter = ({
  event,
}: EventFooterProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const { project } = event;

  const [showPrivacyStatement, setShowPrivacyStatement] = useState(false);

  return (
    <>
      <UI.Div sc={{ textAlign: 'center' }}>
        <OrganizerInfo event={event} />

        {!showPrivacyStatement && (
          <UI.Div sc={{ mt: 2 }}>
            <UI.A
              onClick={() => setShowPrivacyStatement((show) => !show)}
              role="button"
            >
              {t('layout.privacy_statement')}
            </UI.A>
          </UI.Div>
        )}
      </UI.Div>

      {showPrivacyStatement && (
        <PrivacyStatementContainer sc={{ outline: 'gray.250' }}>
          <UI.A
            onClick={() => setShowPrivacyStatement(false)}
            sc={{ pullRight: true, color: 'gray.400', inlineBlock: true, padding: 0.5 }}
          >
            <UI.Icon>
              <X />
            </UI.Icon>
          </UI.A>
          {project.privacy_statement && (
          <>
            <UI.HTML html={project.privacy_statement} />
            <UI.HR />
          </>
          )}
          <UI.H3>
            {t('layout.atleta')}
          </UI.H3>
          <UI.Paragraph>
            {t('layout.atleta_privacy_statement_intro', { organizer: project.organisation_name })}
          </UI.Paragraph>
          <UI.A
            href={`${config.baseUrl}/${locale}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('layout.read_atletas_privacy_statement')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.A>
        </PrivacyStatementContainer>
      )}
    </>
  );
};

const PrivacyStatementContainer = styled(UI.Card)`
  font-size: ${12 / 14}em;

  h1 {
    font-weight: 600;
    font-size: 1.5em;
  }

  h2 {
    font-weight: 600;
    font-size: 1.333em;
  }

  h3 {
    font-weight: 600;
    font-size: 1.25em;
  }
`;

export const AtletaFooter = () => {
  const { t } = useTranslation();

  return (
    <UI.FlexContainer sc={{ justifyContent: 'center', alignItems: 'center' }}>
      <a href={config.baseUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={assetUrl('images/logo-color.svg')}
          alt={t('layout.atleta')}
          width={65}
          height={14}
        />
      </a>
    </UI.FlexContainer>
  );
};

export const TitleBar = styled(UI.Div)`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 90px 1fr 90px;
    background: ${theme.getColor('secondary.300/25')};
    color: ${theme.mix(theme.colors.secondary[300], theme.colors.gray[400], 0.5)};
    margin-bottom: -15px;
    z-index: -1;
    height: 60px;
    align-items: center;
    padding-bottom: 15px;
    border-top-left-radius: ${theme.borderRadiuses.xl}px;
    border-top-right-radius: ${theme.borderRadiuses.xl}px;

    // Back button
    > ${UI.Div}:first-child {
      padding-left: ${theme.gutter * 0.75}px;

      a {
        height: 45px;
        transition: all 0.15s ease-in-out;
        display: inline-flex;
        align-items: center;

        ${UI.Icon} {
          border-radius: 100%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${theme.transparentize(theme.colors.secondary[500], 0.3)};
          color: ${theme.getTitleColor(theme.colors.secondary[500])};
          transition: all 0.15s ease-in-out;
          margin: 0;
          font-size: 1rem;
        }

        &:hover {
          ${UI.Icon} {
            background: ${theme.colors.secondary[500]};
          }
        }
      }
    }

    // Step title
    > ${UI.Div}:nth-child(2) {
      color: ${theme.mix(theme.colors.secondary[500], theme.colors.gray[800], 0.8)};
      text-align: center;
      font-weight: 500;
    }

    // Step number
    > ${UI.Div}:nth-child(3) {
      color: ${theme.mix(theme.colors.secondary[500], theme.colors.gray[800], 0.8)};
      text-align: right;
      padding: 0 ${theme.gutter * 0.75}px;
      font-size: ${12 / 16}rem;
      font-weight: 400;
    }

    @media ${theme.devices.md} {
      // Back button
      > ${UI.Div}:first-child {
        padding-left: ${theme.gutter}px;
      }

      // Step number
      > ${UI.Div}:nth-child(3) {
        padding-right: 0 ${theme.gutter}px;
      }
    }
  `}
`;

export default Layout;
